import { api } from '@goaltree/backend/convex/_generated/api'
import {
  CELEBRATION_ICON,
  CELEBRATION_STYLES,
  type CelebrationStyle,
  FREE_CELEBRATIONS,
} from '@goaltree/backend/convex/lib/celebration'
import { cn } from '@goaltree/ui/lib/utils'
import { useConvexAuth, useQuery } from 'convex/react'
import { LockIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useIsPro } from '@/hooks/use-is-pro'
import { useUpdatePreferences } from '@/hooks/use-update-preferences'
import { celebrate } from '@/lib/celebrate'

// Check-off celebration style chips: tap previews the style, then saves it
// (or shows the teaser and calls onLocked for Pro styles on free plans).
export function CelebrationChips({
  onLocked,
  size = 32,
  stretch,
  className,
}: {
  onLocked: () => void
  size?: number
  stretch?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const { isAuthenticated } = useConvexAuth()
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip') as
    | { celebrationStyle?: CelebrationStyle }
    | null
    | undefined
  const updatePrefs = useUpdatePreferences()
  const { isPro } = useIsPro()
  const current = prefs?.celebrationStyle ?? 'classic'
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {CELEBRATION_STYLES.map((s) => {
        const locked = !isPro && !FREE_CELEBRATIONS.includes(s)
        return (
          <button
            key={s}
            type="button"
            title={t(`settings.celebrationStyle.${s}`)}
            aria-label={t(`settings.celebrationStyle.${s}`)}
            aria-pressed={current === s}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect()
              celebrate({
                x: r.left + r.width / 2,
                y: r.top + r.height / 2,
                style: s === 'surprise' ? undefined : s,
              })
              if (locked) {
                setTimeout(onLocked, 600)
                return
              }
              void updatePrefs({ celebrationStyle: s }).catch(() => {})
            }}
            className={cn(
              'relative flex shrink-0 cursor-pointer items-center justify-center rounded-full border transition-all hover:scale-110 active:scale-95',
              current === s ? 'border-primary bg-primary/15' : 'border-border/60 bg-muted/30',
            )}
            style={{
              width: stretch ? undefined : size,
              height: size,
              flex: stretch ? 1 : undefined,
              fontSize: Math.round(size * 0.5),
            }}
          >
            {CELEBRATION_ICON[s]}
            {locked && (
              <span className="absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-card">
                <LockIcon className="h-2 w-2 text-muted-foreground" strokeWidth={3} />
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
